/**
 * assignCompaniesToCampaign
 *
 * Assigns a set of companies (by chain-proxy id) to a campaign at the company
 * level, via the generic chain-proxy assign endpoint:
 *
 *   POST contacts/contactcompanies/campaigns/assign/
 *   body: { campaign_id, include_ids, exclude_ids, assign: true }
 *
 * This mirrors the payload shape used by `assignToCampaign` in
 * `services/common_table_actions.ts`, but is intentionally a plain
 * `clientInstance.post` rather than the data-table mutation hook
 * (`useAssignToCampaignMutation`): that hook is coupled to a DataTable's
 * optimistic cache, which the import wizard does not have.
 *
 * Used by ReviewImportStep after a successful "Import to a campaign" run, with
 * the distinct SET_CHAIN_PROXY_ID values of the imported rows.
 */
import { clientInstance } from 'utils/http-client'
import { baseAPICall } from 'services/baseAPICall'

const ASSIGN_ENDPOINT = 'contacts/contactcompanies/campaigns/assign/'

/**
 * Assign the given companies to a campaign.
 *
 * @param campaignId    - Target campaign id.
 * @param chainProxyIds - Chain-proxy ids of the companies to assign.
 */
export async function assignCompaniesToCampaign(
  campaignId: number,
  chainProxyIds: number[]
): Promise<void> {
  // Suppress baseAPICall's error toast — ReviewImportStep surfaces a single
  // non-blocking inline note on failure instead, since the contacts are already
  // imported regardless of whether this assignment succeeds.
  await baseAPICall(
    () =>
      clientInstance.post(ASSIGN_ENDPOINT, {
        campaign_id: campaignId,
        include_ids: chainProxyIds,
        exclude_ids: [],
        assign: true,
      }),
    { disableNotifyError: true, disableNotifyGenericError: true }
  )
}
